import { SBModelComponent } from '@supplement-bacon/react-toolkit/models'
import {
  Card,
  Col,
  Divider,
  Progress,
  Row,
  Skeleton,
  Space,
  Statistic,
  theme,
  Typography,
} from 'antd'
import { FC, useEffect, useState } from 'react'
import { Event, EventParticipationStats } from '../../models/Event'
import { fetchEventStats } from '../../services/EventService'
import { useAppSelector } from '../../store/hooks'
import { selectEventById } from '../../store/membership/EventReducer'

const { Text } = Typography

type Props = SBModelComponent<Event> & {}

const EventStats: FC<Props> = ({ id, model }) => {
  const { token } = theme.useToken()
  const item = model ?? useAppSelector(selectEventById(id!))
  const [stats, setStats] = useState<EventParticipationStats | undefined>()
  const [isLoading, setIsLoading] = useState(true)

  const calculatePercentage = (
    numerator: number,
    denominator: number
  ): number => {
    return Math.min(100, Math.round((numerator * 100) / denominator))
  }

  useEffect(() => {
    if (!item) return
    setIsLoading(true)
    fetchEventStats(item.id)
      .then((res) => {
        setStats(res.data)
      })
      .finally(() => setIsLoading(false))
  }, [item])

  if (!item) return null

  if (isLoading || !stats) {
    return (
      <Card>
        <Skeleton.Node active style={{ width: 500 }} />
      </Card>
    )
  }

  const membersBooked = stats.membersCount ?? 0
  const guestsBooked = stats.guestsCount ?? 0
  const maxMembers = stats.maxMembers || 0
  const maxGuests = stats.maxGuests || 0
  const maxParticipants = stats.maxParticipants || 1

  return (
    <Card style={{ borderRadius: 12 }}>
      <Row gutter={[24, 24]}>
        <Col span={6}>
          <Statistic title="Booked" value={stats.participationsCount ?? '--'} />
        </Col>
        <Col span={6}>
          <Statistic title="Available" value={stats.available ?? '--'} />
        </Col>
        <Col span={12}>
          <Space orientation="vertical" className="w-full">
            <Text type="secondary">Booking rate</Text>
            <Progress
              strokeColor={token.colorPrimary}
              percent={calculatePercentage(
                stats.participationsCount,
                maxParticipants
              )}
            />
          </Space>
        </Col>
      </Row>

      <Divider />

      <Row gutter={[24, 24]}>
        <Col span={24}>
          <Space
            orientation="vertical"
            style={{ width: '100%', marginTop: 8 }}
            size={16}
          >
            <Space orientation="vertical" style={{ width: '100%' }} size={0}>
              <Text type="secondary">Members</Text>
              <Progress
                strokeColor={token.colorPrimary}
                percent={calculatePercentage(membersBooked, maxMembers)}
                style={{ marginTop: 4 }}
              />
              <Space size={12} style={{ marginTop: 4 }}>
                <Text type="secondary">
                  Booked: {membersBooked} / {maxMembers}
                </Text>
              </Space>
            </Space>

            <Space orientation="vertical" style={{ width: '100%' }} size={0}>
              <Text type="secondary">Guests</Text>
              <Progress
                strokeColor={token.colorPrimary}
                percent={calculatePercentage(guestsBooked, maxGuests)}
                style={{ marginTop: 4 }}
              />
              <Space size={12} style={{ marginTop: 4 }}>
                <Text type="secondary">
                  Booked: {guestsBooked} / {maxGuests}
                </Text>
                <Text italic type="secondary">
                  Max {stats.maxGuestsPerParticipant} / member
                </Text>
              </Space>
            </Space>
          </Space>
        </Col>
      </Row>
    </Card>
  )
}

type EventStatsType = { Stats: typeof EventStats }

export { EventStats, type EventStatsType }
